import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { Form, useFormikContext, getIn } from 'formik';
import map from 'lodash/map';
import { FarmRiskType, lookupsJson } from '@ourbranch/lookups';

import { useRouteHelper } from 'core/hooks/use-route-helper';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import { isFarmRiskRemovable } from 'common/helpers/farm-helpers';
import editIcon from 'core/assets/svg/edit.svg';
import pencilOrangeIcon from 'core/assets/svg/pencil-orange.svg';
import CollapsibleCard from 'core/components/collapsible-card';
import { FormField } from 'core/components/form';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { Banner } from 'common/components/banner';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import { useFarmOpenSections } from 'common/hooks/farm-open-sections-context';
import { riskTypes } from './risks/risk-types';
import { formatLocationAddress, isCountyIncludedInFormattedAddress } from './helpers';
import AddRisk from './risks/add-risk';
import { FarmingEquipment } from './farming-equipment';
import { EditRiskModal } from './edit-risk-modal';

import useStyles from './location.styles';

const fpcToCustomPpcOptions = lookupsJson.farm.fpcToCustomPpcOptions;
const locationLabelLookup = {
  street: 'Street Address',
  'section-township-range': 'Section/Township/Range',
  'lot-block': 'Lot/Block',
  'long-legal': 'Long legal'
};

const Location = observer(({ fieldName, item, index, setLocationIndexToRemove, setLocationIndexToEdit }) => {
  const classes = useStyles();
  const {
    offer: { offer }
  } = useStore();
  const rejections = offer?.rejections || [];
  const { disableForm: disableLocationForm, setActiveForm } = useActiveFormState(fieldName);
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const { inPolicyRoute } = useRouteHelper();
  // Underwriters may remove the otherwise-locked primary structure (location 0, risk 0). (#32513)
  const { canApproveUWReview } = useSession();

  const { openLocationIndices, toggleLocation, addOpenRisk, resetToFirstRisk } = useFarmOpenSections();
  const [riskFieldnameToEdit, setRiskFieldnameToEdit] = useState(null);

  const removeRisk = (riskIndex) => {
    const newRisks = item.risks.filter((_, checkIndex) => checkIndex !== riskIndex);
    setFieldValue(`${fieldName}.risks`, newRisks);
    setFieldTouched(`${fieldName}.risks`);
    setActiveForm(null);
    // Intentionally resets to only risks[0] open rather than re-indexing surviving risk fieldNames
    // (which would mirror removeLocation's shift logic). We lean toward collapsing on removal.
    resetToFirstRisk(index);
  };

  const hasBeenRated = !!item.id;
  const needMoreData = rejections.some((rejection) => rejection.code === '50006');

  const isLocationFormDisabled = Boolean(disableLocationForm);

  return (
    <>
      {riskFieldnameToEdit !== null && (
        <EditRiskModal onClose={() => setRiskFieldnameToEdit(null)} fieldName={riskFieldnameToEdit} />
      )}
      <CollapsibleCard
        content={
          <div className={classes.header}>
            <div className={classes.headerRow}>
              <Label type="infoTitle">{locationLabelLookup[item.legalDescription.format]}</Label>
              <div className={classes.divider} />
              <Label type="subtitleSmall">{item.description}</Label>
              <IconButton onClick={() => setLocationIndexToEdit(index)} className={classes.editButton}>
                <img className={classes.editIcon} src={editIcon} alt="Edit row" />
              </IconButton>
            </div>
            <div className={classes.headerRow}>
              <Label type="infoTitleSmall">{formatLocationAddress(item.address, item.legalDescription)}</Label>
              <div className={classes.divider} />
              {!isCountyIncludedInFormattedAddress(item.legalDescription) && item.address?.county && (
                <>
                  <Label type="infoTitleSmall">{`${item.address.county} County`}</Label>
                  <div className={classes.divider} />
                </>
              )}
              <Label type="infoTitleSmall">{item.numberOfAcres} acres</Label>
            </div>
          </div>
        }
        id={fieldName}
        isRemovable={index !== 0}
        onDelete={() => setLocationIndexToRemove(index)}
        disableRemoveButton={isLocationFormDisabled}
        open={openLocationIndices.has(index)}
        onChange={() => {
          toggleLocation(index);
        }}
      >
        <Form>
          {!hasBeenRated && (
            <Banner
              variant="notification"
              title={
                inPolicyRoute
                  ? "We'll pull data for this location—go ahead and preview changes."
                  : "We'll pull data for this location—go ahead and update the offer."
              }
              subtitle="Or enter the details manually if you prefer."
            />
          )}
          {hasBeenRated && needMoreData && (
            <Banner
              variant="warning"
              title="Data for this location isn't available—please enter the details manually."
              subtitle={
                inPolicyRoute
                  ? "This information won't auto-fill, even if you preview changes."
                  : "This information won't auto-fill, even if you update the offer."
              }
            />
          )}
          <Grid spacing={4} container justifyContent="space-between" className={classes.card}>
            <Grid item xs={12}>
              <Label type="infoTitleSmall">Location</Label>
            </Grid>
            {hasBeenRated && (
              <>
                <FormField
                  name={`${fieldName}.fpc`}
                  type="value"
                  label="Fire Protection Class"
                  value={item.fpc}
                  xs={4}
                  disabled={isLocationFormDisabled}
                />
                <FormField
                  name={`${fieldName}.distanceToFireDept`}
                  type="value"
                  label="Miles to Fire Dept"
                  value={item.distanceToFireDept || 'N/A'}
                  xs={4}
                  disabled={isLocationFormDisabled}
                />
                <FormField
                  name={`${fieldName}.fireHydrantWithin1000ft`}
                  type="checkbox"
                  label="Fire hydrant within 1,000 feet?"
                  mode="light"
                  xs={4}
                  disabled={isLocationFormDisabled}
                />
              </>
            )}
            {!hasBeenRated && (
              <>
                <Field
                  type="select"
                  id={`${fieldName}.fpc`}
                  name={`${fieldName}.fpc`}
                  label="Fire Protection Class"
                  mode="light"
                  xs={4}
                  options={fpcToCustomPpcOptions}
                  disabled={isLocationFormDisabled}
                />
                <Field
                  type="numeric"
                  id={`${fieldName}.distanceToFireDept`}
                  name={`${fieldName}.distanceToFireDept`}
                  label="Miles to Fire Dept"
                  mode="light"
                  xs={4}
                  disabled={isLocationFormDisabled}
                />
                <Field
                  type="checkbox"
                  className={classes.centerCheckbox}
                  id={`${fieldName}.fireHydrantWithin1000ft`}
                  name={`${fieldName}.fireHydrantWithin1000ft`}
                  label="Fire hydrant within 1,000 feet?"
                  mode="light"
                  xs={4}
                  disabled={isLocationFormDisabled}
                />
                <Field
                  name={`${fieldName}.latitude`}
                  id={`${fieldName}.latitude`}
                  type="string"
                  label="Latitude"
                  mode="light"
                  xs={6}
                  disabled={isLocationFormDisabled}
                />
                <Field
                  name={`${fieldName}.longitude`}
                  id={`${fieldName}.longitude`}
                  type="string"
                  label="Longitude"
                  mode="light"
                  xs={6}
                  disabled={isLocationFormDisabled}
                />
              </>
            )}

            <FormField
              name={`${fieldName}.additionalLegalDescription`}
              type="value"
              label="Additional Legal Description (optional)"
              value={item.additionalLegalDescription || 'Not provided'}
              xs={12}
              disabled={isLocationFormDisabled}
              action={{
                icon: pencilOrangeIcon,
                onClick: () => setLocationIndexToEdit(index),
                alt: 'Edit additional legal description',
                disabled: isLocationFormDisabled
              }}
            />
            <FormField
              name={`${fieldName}.locationDirections`}
              type="value"
              label="Location Directions (optional)"
              value={item.locationDirections || 'Not provided'}
              xs={12}
              disabled={isLocationFormDisabled}
              action={{
                icon: pencilOrangeIcon,
                onClick: () => setLocationIndexToEdit(index),
                alt: 'Edit location directions',
                disabled: isLocationFormDisabled
              }}
            />

            {/* psuedo-formlist for all risks */}
            {hasBeenRated && (
              <Grid container>
                {/* title */}
                <Grid container justifyContent="space-between">
                  <Label type="sectionSubtitle">Dwellings and structures</Label>
                  <Grid item className={classes.rightContainer}>
                    <Label type="body4" className={classes.rightLabel}>
                      Total
                    </Label>
                    <Label type="body4">{`${item.risks?.length || 0} ${item.risks?.length === 1 ? 'risk' : 'risks'}`}</Label>
                  </Grid>
                </Grid>

                {map(item.risks, (risk, riskIndex) => {
                  if (risk.type === FarmRiskType.Fo6Scheduled || risk.type === FarmRiskType.Fo6Unscheduled) {
                    return null;
                  }

                  const RiskComponent = riskTypes[risk.type];
                  return (
                    <RiskComponent
                      item={risk}
                      index={riskIndex}
                      key={riskIndex}
                      fieldName={`${fieldName}.risks[${riskIndex}]`}
                      // The first risk of the first location (the primary structure) is locked —
                      // except for underwriters, who may remove it and let the agent re-add. (#32513)
                      canRemoveRisk={isFarmRiskRemovable({
                        locationIndex: index,
                        riskIndex,
                        canApproveUWReview
                      })}
                      removeRisk={removeRisk}
                      setRiskFieldnameToEdit={setRiskFieldnameToEdit}
                      locationAddress={item.address}
                    />
                  );
                })}

                <AddRisk
                  locationAddress={item.address}
                  onAdd={(val) => {
                    const item = val;
                    Object.keys(item).forEach((key) => {
                      if (item[key] === '') {
                        item[key] = null;
                      }
                    });
                    const list = getIn(values, `${fieldName}.risks`);
                    setFieldValue(`${fieldName}.risks`, [...list, item]);
                    setFieldTouched(`${fieldName}.risks`);

                    const newIndex = list.length;
                    setActiveForm(`${fieldName}.risks[${newIndex}]`);
                    addOpenRisk(index, `${fieldName}.risks[${newIndex}]`);
                  }}
                  fieldName={`${fieldName}-addRisk`}
                  disabled={isLocationFormDisabled}
                />
                <FarmingEquipment fieldName={fieldName} />
              </Grid>
            )}
          </Grid>
        </Form>
      </CollapsibleCard>
    </>
  );
});
Location.propTypes = {
  fieldName: PropTypes.string.isRequired,
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  setLocationIndexToRemove: PropTypes.func.isRequired,
  setLocationIndexToEdit: PropTypes.func.isRequired
};

Location.defaultProps = {
  disabled: false
};

export default Location;
